'use client'

import { motion, useWillChange } from 'motion/react'
import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { PaginationDots } from '@/components/pagination-dots'
import { throttle } from '@/utils/functions/throttle'
import { cn } from '@/utils/styles/cn'

interface IVideoSliderProps {
    videoPaths: Record<string, Record<string, string>>
    className?: string
}

export function VideoSlider({ videoPaths, className }: IVideoSliderProps) {
    const mobileScrollContainerRef = useRef<HTMLDivElement>(null)
    const desktopContainerRef = useRef<HTMLDivElement>(null)
    const [hoveredVideo, setHoveredVideo] = useState<string | null>(null)

    const [currentImagePage, setCurrentImagePage] = useState(1)
    const [maxHeight, setMaxHeight] = useState(0)

    const desktopRefs = useRef<Record<number, HTMLDivElement>>({})
    const mobileRefs = useRef<Record<number, HTMLDivElement>>({})

    const makeRef = useCallback(
        (type: 'mobile' | 'desktop') => (index: number) => (element: HTMLDivElement) => {
            if (!element) return

            switch (type) {
                case 'mobile':
                    mobileRefs.current[index] = element
                    break
                case 'desktop':
                    desktopRefs.current[index] = element
                    break
            }
        },
        []
    )

    useLayoutEffect(() => {
        const calculateMaxHeight = () => {
            const desktopHeights = Object.values(desktopRefs.current).map((ref) => ref.clientHeight)
            const mobileHeights = Object.values(mobileRefs.current).map((ref) => ref.clientHeight)

            const allHeights = [...desktopHeights, ...mobileHeights]
            setMaxHeight(Math.max(0, ...allHeights))
        }

        calculateMaxHeight()

        const resizeObserver = new ResizeObserver(calculateMaxHeight)
        Object.values(desktopRefs.current).forEach((ref) => ref && resizeObserver.observe(ref))
        Object.values(mobileRefs.current).forEach((ref) => ref && resizeObserver.observe(ref))

        return () => {
            resizeObserver.disconnect()
        }
    }, [])

    const onScroll = throttle(() => {
        const container = mobileScrollContainerRef.current

        if (!container) return

        const pageWidth = container.clientWidth
        const currentPage = Math.round(container.scrollLeft / pageWidth) + 1

        if (currentPage !== currentImagePage) {
            setCurrentImagePage(currentPage)
        }
    }, 200)

    const scrollToPage = useCallback((page: number) => {
        const container = mobileScrollContainerRef.current

        if (!container) return

        const pageWidth = container.clientWidth
        container.scrollTo({
            left: (page - 1) * pageWidth,
            behavior: 'smooth',
        })
        setCurrentImagePage(page)
    }, [])

    const willChange = useWillChange()

    return (
        <div className="w-full flex flex-col items-center relative pb-6">
            <div ref={desktopContainerRef} className={cn(`hidden md:flex md:px-4`, className)}>
                <div className="hidden md:flex gap-4">
                    {Object.entries(videoPaths).map(([city, { url, thumbnail, ariaLabel }]) => (
                        <div
                            key={city}
                            className="w-full h-[40vw]"
                            onMouseEnter={() => setHoveredVideo(city)}
                            onMouseLeave={() => setHoveredVideo(null)}
                        >
                            <video
                                className="w-full h-auto object-contain rounded-2xl"
                                loop
                                muted
                                playsInline
                                controls={hoveredVideo === city}
                                poster={thumbnail}
                                src={url}
                                aria-label={ariaLabel}
                            />
                        </div>
                    ))}
                </div>
            </div>

            <motion.div
                initial={{ height: maxHeight }}
                animate={{ height: maxHeight }}
                transition={{
                    type: 'spring',
                    stiffness: 900,
                    damping: 70,
                }}
                style={{ willChange }}
                className="w-dvw md:hidden"
            >
                <motion.div
                    onScroll={onScroll}
                    ref={mobileScrollContainerRef}
                    className={cn(
                        `flex md:hidden md:absolute flex-row items-start gap-x-0 overflow-y-hidden overflow-x-auto snap-x
                                    snap-mandatory scrollbar-hide max-w-[100vw]`
                    )}
                >
                    {Object.entries(videoPaths).map(
                        ([city, { url, thumbnail, ariaLabel }], index) => (
                            <div
                                key={city}
                                className="flexrelative w-full h-full"
                                onMouseEnter={() => setHoveredVideo(city)}
                                onMouseLeave={() => setHoveredVideo(null)}
                            >
                                <VideoPage
                                    index={index}
                                    url={url}
                                    thumbnail={thumbnail}
                                    ariaLabel={ariaLabel}
                                    makeRef={makeRef('mobile')}
                                    key={`mobile-${index}`}
                                />
                            </div>
                        )
                    )}
                </motion.div>
                <PaginationDots
                    currentPage={currentImagePage}
                    pagesCount={Object.keys(videoPaths).length}
                    className="absolute bottom-6 left-0 right-0 flex justify-center pb-4"
                    activeColorClassName="bg-black scale-2"
                    inactiveColorClassName="bg-black/50"
                    onPageChange={(page) => {
                        scrollToPage(page)
                    }}
                />
            </motion.div>
        </div>
    )
}

interface IVideoPageProps {
    url: string
    thumbnail: string
    ariaLabel: string
    index: number
    makeRef: (index: number) => (element: HTMLDivElement) => void
}

const VideoPage = ({ url, thumbnail, ariaLabel, index, makeRef }: IVideoPageProps) => {
    return (
        <div
            key={'videos-slider' + ariaLabel + index}
            className="relative z-[0] flex md:flex-row flex-col flex-shrink-0 snap-center snap-always md:snap-none
                        min-w-[100vw] max-w-[100vw] justify-center items-center md:items-start px-4 md:px-0"
        >
            <div
                ref={makeRef(index)}
                className="flex md:flex-row flex-col justify-center items-center w-full bg-black/5 md:bg-white gap-6
                rounded-[16px] pt-4 pb-12 px-4 md:py-0 md:px-0"
            >
                <video
                    className="w-full h-full object-contain"
                    loop
                    muted
                    controls
                    poster={thumbnail}
                    src={url}
                    aria-label={ariaLabel}
                />
            </div>
        </div>
    )
}
